import { useCallback, useEffect, useState } from 'react';
/**
 * Pure Web Audio API — no network, no assets. Synthesises short game-style
 * feedback chirps, thuds and fanfares on the fly.
 *
 * iOS Safari has three traps that block audio:
 *   1. AudioContext **must** be created inside a user gesture.
 *   2. `resume()` **must** be called synchronously inside a user gesture.
 *   3. Web Audio output is routed to the "ambient" session by default,
 *      which is silenced by the physical silent switch. Playing a tiny
 *      silent `<audio playsinline>` element during the same gesture
 *      switches the page to the "playback" session and bypasses it.
 *
 * Strategy:
 *   - On module import we install global capture-phase listeners for
 *     touchstart / pointerdown / mousedown / keydown.
 *   - The first one fires → create AudioContext + resume + play a silent
 *     HTML5 audio blip + schedule a silent oscillator tick. All
 *     synchronous, all inside the gesture. Listeners remove themselves.
 *   - After that, any subsequent `play()` just writes to the already-
 *     unlocked context.
 */
// --- Module singletons --------------------------------------------------
let sharedCtx = null;
let masterGain = null;
let compressor = null;
let unlockInstalled = false;
let unlocked = false;
let silentAudio = null;
const unlockListeners = new Set();
// 1-sample silent WAV (44-byte header + 0 bytes of PCM). Playing this in an
// HTML5 audio tag with `playsinline` tells iOS Safari to use the "playback"
// audio session, which ignores the silent switch.
const SILENT_WAV = 'data:audio/wav;base64,UklGRiUAAABXQVZFZm10IBAAAAABAAEAiBMAABAnAAACABAAZGF0YQEAAAAA';
function createCtx() {
    if (typeof window === 'undefined')
        return null;
    const Ctx = window.AudioContext ??
        window.webkitAudioContext;
    if (!Ctx)
        return null;
    try {
        const ctx = new Ctx();
        const mg = ctx.createGain();
        mg.gain.value = 0.85;
        const comp = ctx.createDynamicsCompressor();
        comp.threshold.value = -16;
        comp.knee.value = 22;
        comp.ratio.value = 5;
        comp.attack.value = 0.004;
        comp.release.value = 0.2;
        mg.connect(comp).connect(ctx.destination);
        masterGain = mg;
        compressor = comp;
        return ctx;
    }
    catch {
        return null;
    }
}
function primeSilentHtmlAudio() {
    // Must be created and played inside the gesture. Keeping a reference and
    // mounting to the DOM is required on some iOS versions to prevent the
    // audio session from being torn down between frames.
    if (silentAudio)
        return;
    try {
        const el = document.createElement('audio');
        el.setAttribute('playsinline', 'true');
        el.setAttribute('webkit-playsinline', 'true');
        el.setAttribute('preload', 'auto');
        el.loop = true;
        el.muted = false;
        el.volume = 0;
        el.src = SILENT_WAV;
        // Hide but keep in DOM
        el.style.position = 'fixed';
        el.style.left = '-1000px';
        el.style.top = '-1000px';
        el.style.width = '1px';
        el.style.height = '1px';
        el.style.opacity = '0';
        document.body.appendChild(el);
        silentAudio = el;
        const p = el.play();
        if (p && typeof p.catch === 'function') {
            p.catch(() => {
                // Some iOS versions only succeed on the next gesture — ignore.
            });
        }
    }
    catch {
        // ignore
    }
}
function installUnlock() {
    if (typeof window === 'undefined')
        return;
    if (unlockInstalled)
        return;
    unlockInstalled = true;
    const unlock = () => {
        // (1) Prime HTML5 audio to switch iOS to playback session
        primeSilentHtmlAudio();
        // (2) Create Web Audio context INSIDE the gesture
        if (!sharedCtx)
            sharedCtx = createCtx();
        if (!sharedCtx) {
            // No audio support — mark unlocked so UI doesn't stay in pending state
            unlocked = true;
            unlockListeners.forEach((cb) => cb());
            cleanupListeners();
            return;
        }
        // (3) Resume synchronously in the gesture
        if (sharedCtx.state === 'suspended') {
            try {
                const p = sharedCtx.resume();
                if (p && typeof p.catch === 'function')
                    p.catch(() => { });
            }
            catch {
                // ignore
            }
        }
        // (4) Schedule a silent web-audio tick — helps some WebKit builds
        try {
            const ctx = sharedCtx;
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            g.gain.value = 0;
            osc.frequency.value = 440;
            osc.connect(g).connect(ctx.destination);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.05);
        }
        catch {
            // ignore
        }
        unlocked = true;
        unlockListeners.forEach((cb) => cb());
        cleanupListeners();
    };
    function cleanupListeners() {
        window.removeEventListener('touchstart', unlock, true);
        window.removeEventListener('touchend', unlock, true);
        window.removeEventListener('pointerdown', unlock, true);
        window.removeEventListener('mousedown', unlock, true);
        window.removeEventListener('keydown', unlock, true);
        window.removeEventListener('click', unlock, true);
    }
    // Passive: false is critical for iOS so preventDefault could work if we
    // ever added it; capture mode ensures we beat React's delegated handlers.
    const opts = { capture: true, passive: true };
    window.addEventListener('touchstart', unlock, opts);
    window.addEventListener('touchend', unlock, opts);
    window.addEventListener('pointerdown', unlock, opts);
    window.addEventListener('mousedown', unlock, opts);
    window.addEventListener('keydown', unlock, opts);
    window.addEventListener('click', unlock, opts);
}
// Install listeners immediately on module import — before any React
// rendering pass. That way the very first tap on the page counts.
if (typeof window !== 'undefined') {
    installUnlock();
}
function getOutput() {
    if (!sharedCtx)
        return null;
    return masterGain ?? sharedCtx.destination;
}
function readMuted() {
    if (typeof window === 'undefined')
        return false;
    if (window.__glSfxMuted)
        return true;
    try {
        return window.localStorage?.getItem('gl:sfx-muted') === '1';
    }
    catch {
        return false;
    }
}
function writeMuted(value) {
    if (typeof window === 'undefined')
        return;
    window.__glSfxMuted = value;
    try {
        window.localStorage?.setItem('gl:sfx-muted', value ? '1' : '0');
    }
    catch {
        // ignore
    }
}
export function useSfx() {
    const [, setTick] = useState(0);
    useEffect(() => {
        function onStorage(e) {
            if (e.key === 'gl:sfx-muted')
                setTick((t) => t + 1);
        }
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);
    const play = useCallback((name) => {
        if (readMuted())
            return;
        const ctx = sharedCtx;
        const out = getOutput();
        if (!ctx || !out)
            return;
        // Safety: if somehow suspended (ex: tab put to background), kick resume.
        if (ctx.state === 'suspended') {
            try {
                const p = ctx.resume();
                if (p && typeof p.catch === 'function')
                    p.catch(() => { });
            }
            catch {
                // ignore
            }
        }
        const now = ctx.currentTime;
        const tone = (freq, startOffset, duration, gain = 0.22, type = 'sine', endFreq) => {
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = type;
            osc.frequency.setValueAtTime(freq, now + startOffset);
            if (endFreq !== undefined) {
                osc.frequency.exponentialRampToValueAtTime(Math.max(1, endFreq), now + startOffset + duration);
            }
            g.gain.setValueAtTime(0, now + startOffset);
            g.gain.linearRampToValueAtTime(gain, now + startOffset + 0.01);
            g.gain.exponentialRampToValueAtTime(0.0001, now + startOffset + duration);
            osc.connect(g).connect(out);
            osc.start(now + startOffset);
            osc.stop(now + startOffset + duration + 0.02);
        };
        const noise = (startOffset, duration, gain = 0.2, filterFreq = 800) => {
            const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * duration), ctx.sampleRate);
            const data = buf.getChannelData(0);
            for (let i = 0; i < data.length; i += 1)
                data[i] = Math.random() * 2 - 1;
            const src = ctx.createBufferSource();
            src.buffer = buf;
            const filter = ctx.createBiquadFilter();
            filter.type = 'bandpass';
            filter.frequency.setValueAtTime(filterFreq, now + startOffset);
            const g = ctx.createGain();
            g.gain.setValueAtTime(0, now + startOffset);
            g.gain.linearRampToValueAtTime(gain, now + startOffset + 0.01);
            g.gain.exponentialRampToValueAtTime(0.0001, now + startOffset + duration);
            src.connect(filter).connect(g).connect(out);
            src.start(now + startOffset);
            src.stop(now + startOffset + duration + 0.02);
        };
        switch (name) {
            case 'correct': {
                tone(659.25, 0, 0.14, 0.2, 'sine');
                tone(783.99, 0.08, 0.16, 0.22, 'sine');
                tone(1046.5, 0.18, 0.3, 0.24, 'sine');
                tone(1318.5, 0.24, 0.34, 0.18, 'triangle');
                break;
            }
            case 'wrong': {
                tone(293.66, 0, 0.2, 0.26, 'triangle');
                tone(220.0, 0.15, 0.3, 0.26, 'sawtooth');
                noise(0, 0.12, 0.06, 400);
                break;
            }
            case 'tap': {
                tone(1400, 0, 0.05, 0.12, 'sine');
                tone(1700, 0.01, 0.05, 0.08, 'triangle');
                break;
            }
            case 'level-up': {
                tone(523.25, 0, 0.12);
                tone(659.25, 0.1, 0.12);
                tone(783.99, 0.2, 0.12);
                tone(1046.5, 0.32, 0.3);
                tone(1318.5, 0.32, 0.3, 0.12, 'triangle');
                break;
            }
            case 'jump': {
                tone(420, 0, 0.14, 0.24, 'square', 900);
                tone(640, 0.02, 0.1, 0.12, 'triangle', 1200);
                noise(0, 0.05, 0.06, 1500);
                break;
            }
            case 'land': {
                tone(180, 0, 0.1, 0.22, 'triangle', 80);
                noise(0, 0.08, 0.1, 400);
                break;
            }
            case 'hit': {
                tone(140, 0, 0.22, 0.32, 'sawtooth', 50);
                noise(0, 0.18, 0.22, 900);
                tone(330, 0.02, 0.1, 0.18, 'square', 110);
                break;
            }
            case 'xp-gain': {
                tone(988, 0, 0.14, 0.18, 'sine');
                tone(1318.5, 0.06, 0.22, 0.18, 'sine');
                tone(1760, 0.12, 0.26, 0.1, 'triangle');
                break;
            }
            case 'whoosh': {
                noise(0, 0.28, 0.16, 1200);
                tone(880, 0, 0.24, 0.08, 'sine', 220);
                break;
            }
            case 'game-over': {
                tone(523.25, 0, 0.18, 0.22, 'triangle');
                tone(415.3, 0.18, 0.2, 0.22, 'triangle');
                tone(329.63, 0.38, 0.22, 0.22, 'triangle');
                tone(261.63, 0.6, 0.5, 0.26, 'sawtooth');
                tone(130.81, 0.6, 0.55, 0.18, 'triangle');
                break;
            }
            case 'start': {
                tone(523.25, 0, 0.1);
                tone(659.25, 0.09, 0.1);
                tone(783.99, 0.18, 0.1);
                tone(1046.5, 0.27, 0.24);
                tone(1318.5, 0.27, 0.26, 0.14, 'triangle');
                noise(0.27, 0.1, 0.04, 2200);
                break;
            }
            case 'heart-loss': {
                tone(330, 0, 0.12, 0.24, 'triangle');
                tone(180, 0.1, 0.26, 0.26, 'triangle');
                noise(0, 0.1, 0.06, 600);
                break;
            }
        }
        void compressor; // keep reference
    }, []);
    const mute = useCallback((value) => {
        writeMuted(value);
        setTick((t) => t + 1);
    }, []);
    const muted = useCallback(() => readMuted(), []);
    const isUnlocked = useCallback(() => unlocked, []);
    return { play, mute, muted, isUnlocked };
}
export function onAudioUnlock(cb) {
    if (unlocked) {
        cb();
        return () => undefined;
    }
    unlockListeners.add(cb);
    return () => {
        unlockListeners.delete(cb);
    };
}
//# sourceMappingURL=useSfx.js.map